import {
  ForbiddenCodeEnum,
  isAuthError,
  isBadRequestError,
  isForbiddenError,
  isInvalidError,
  isNotFoundError,
  isPaymentRequiredError,
  isSuccess,
} from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import {
  UpdateApInvoiceTransactionBasicItem200,
  UpdateApInvoiceTransactionBasicItemBodyApInvoiceTransactionBasicItem,
  UpdateApInvoiceTransactionBasicItemMutationError,
  getGetApInvoiceTransactionBasicItemQueryKey,
  getGetApInvoiceTransactionGeneralInputsQueryKey,
  useGetApInvoiceTransactionBasicItemSuspense,
  useUpdateApInvoiceTransactionBasicItem,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

export const useGetBasicTransactionItemDetail = (id: string) => {
  const { data } = useGetApInvoiceTransactionBasicItemSuspense(id);

  if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
    // @ts-ignore - data.data might not have code property in success types
    if (data.data?.code !== ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE)
      location.href = data.headers?.location ?? '';
  }

  return {
    data: data?.data?.ap_invoice_transaction_basic_item,
  };
};

export const useUpdateBasicTransactionItem = (reportTypeId?: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useUpdateApInvoiceTransactionBasicItem();

  const mutateAsync = useCallback(
    async (
      itemId: string | undefined,
      args: UpdateApInvoiceTransactionBasicItemBodyApInvoiceTransactionBasicItem
    ) => {
      if (itemId === undefined) {
        throw new Error();
      }

      // The custom Axios clients treats status codes < 500 as successful.
      // We need to tell Typescript that even a successfully resolved Promise
      // may contain an error response object.
      const response: AxiosResponse<
        | UpdateApInvoiceTransactionBasicItem200
        | UpdateApInvoiceTransactionBasicItemMutationError
      > = await _mutateAsync({
        id: itemId,
        data: { ap_invoice_transaction_basic_item: args },
      });

      if (
        isSuccess(response.status) &&
        'ap_invoice_transaction_basic_item' in response.data
      ) {
        await queryClient.invalidateQueries({
          queryKey: getGetApInvoiceTransactionBasicItemQueryKey(itemId),
        });

        if (reportTypeId !== undefined) {
          await queryClient.invalidateQueries({
            queryKey:
              getGetApInvoiceTransactionGeneralInputsQueryKey(reportTypeId),
          });
        }

        return {
          code: response.status,
          data: response.data,
        };
      } else if (isCommonError(response.data)) {
        return {
          code: response.status,
          isError: true,
          isBadRequestError: !!isBadRequestError(response.status),
          isAuthError: !!isAuthError(response.status),
          isPaymentRequiredError: !!isPaymentRequiredError(response.status),
          isForbiddenError: !!isForbiddenError(response.status),
          isNotFoundError: !!isNotFoundError(response.status),
          isInvalidError: !!isInvalidError(response.status),
          isForbiddenMimicError:
            response.data.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE,
          messages: response.data.messages ?? [],
        };
      } else {
        throw response;
      }
    },
    [_mutateAsync, reportTypeId, queryClient]
  );

  return { mutateAsync };
};
